import '../assets/ContentPage.css';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { useContentLayoutStore } from './zustand-stores';
import { createContext, useContext, useMemo, useState } from 'react';
import { useCompactViewport } from '../general/use-compact-viewport';

const ContentPageNavigation = createContext({ showDetails: () => {} });
export const useContentPageNavigation = () => useContext(ContentPageNavigation);

type PanelContent = React.ReactNode | ((navigateToOtherPanel: () => void) => React.ReactNode);

interface ContentPageProps {
    pageId: string;              // unique per page
    pageTitle: string;
    pageTitleLevel?: 'h1' | 'h2';
    leftPanelTitle: string;
    rightPanelTitle: string;
    rightPanelHeading?: React.ReactNode;
    leftContent?: PanelContent;
    rightContent?: PanelContent;
    leftContentPercent?: number;
    mobileTabs?: boolean;
    publicPageTitle?: boolean;
    initialMobilePanel?: 'left' | 'right';
    mobileBackLabel?: string;
    mobileRightPanelTitle?: string;
}

export function ContentPage({
    pageId,
    pageTitle,
    pageTitleLevel = 'h2',
    leftPanelTitle,
    rightPanelTitle,
    rightPanelHeading,
    leftContent,
    rightContent,
    leftContentPercent,
    mobileTabs = false,
    publicPageTitle = false,
    initialMobilePanel = 'left',
    mobileBackLabel,
    mobileRightPanelTitle = rightPanelTitle
}: ContentPageProps) {

    const leftPercent: number = Math.min(Math.max(10, (leftContentPercent ?? 50)), 50);
    const defaultSizes = useMemo(() => [leftPercent, 100 - leftPercent], []);
    const sizes = useContentLayoutStore(
        (s) => s.splitterSizesByPage[pageId] ?? defaultSizes
    );
    const setSizes = useContentLayoutStore((s) => s.setSplitterSizes);
    const isSmallScreen = useCompactViewport();
    const [activeMobilePanel, setActiveMobilePanel] = useState<'left' | 'right'>(initialMobilePanel);

    const leftPanelContent = typeof leftContent === 'function'
        ? leftContent(() => setActiveMobilePanel('right'))
        : leftContent;
    const rightPanelContent = typeof rightContent === 'function'
        ? rightContent(() => setActiveMobilePanel('left'))
        : rightContent;

    const PageHeading = pageTitleLevel;
    return (
        <ContentPageNavigation.Provider value={{ showDetails: () => {
            if (mobileTabs && isSmallScreen) setActiveMobilePanel('right');
        } }}>
        <div className={`content-page${mobileTabs && isSmallScreen ? ' content-page-compact' : ''}${mobileBackLabel ? ' content-page-management' : ''}`}>
            <PageHeading className={`page-title${publicPageTitle ? ' public-page-title theme-content' : ''}`}>{pageTitle}</PageHeading>

            {mobileTabs && isSmallScreen ? <div className="content-page-mobile">
                <div className="content-page-mobile-tabs" role="group" aria-label={`${pageTitle} view`}>
                    <button type="button" aria-pressed={activeMobilePanel === 'left'}
                        onClick={() => setActiveMobilePanel('left')}>{leftPanelTitle}</button>
                    <button type="button" aria-pressed={activeMobilePanel === 'right'}
                        onClick={() => setActiveMobilePanel('right')}>{rightPanelHeading ?? mobileRightPanelTitle}</button>
                </div>
                <section className="content-page-mobile-panel" aria-label={leftPanelTitle} hidden={activeMobilePanel !== 'left'}>
                    <div className="panel">
                        <div className="panel-body">{leftPanelContent}</div>
                    </div>
                </section>
                <section className="content-page-mobile-panel" aria-label={rightPanelTitle} hidden={activeMobilePanel !== 'right'}>
                    <div className="panel">
                        <div className="panel-body">
                            {mobileBackLabel && <button type="button" className="management-details-back"
                                onClick={() => setActiveMobilePanel('left')}><i className="pi pi-arrow-left" aria-hidden="true" /> {mobileBackLabel}</button>}
                            {rightPanelContent}
                        </div>
                    </div>
                </section>
            </div> : <div className="splitter-container">
                <Splitter
                    layout="horizontal"
                    onResizeEnd={(e) => setSizes(pageId, e.sizes)}
                >
                    <SplitterPanel size={sizes[0]} minSize={10}>
                        <div className="panel">
                            <div className="panel-header">{leftPanelTitle}</div>
                            <div className="panel-body">{leftPanelContent}</div>
                        </div>
                    </SplitterPanel>

                    <SplitterPanel size={sizes[1]} minSize={10}>
                        <div className="panel">
                            <div className="panel-header">{rightPanelHeading ?? rightPanelTitle}</div>
                            <div className="panel-body">{rightPanelContent}</div>
                        </div>
                    </SplitterPanel>
                </Splitter>
            </div>}
        </div>
        </ContentPageNavigation.Provider>
    );
}
